import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { products, categories } from '../data/products';

const Products = () => {
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredProducts = activeCategory === 'All' 
    ? products 
    : products.filter(p => p.category === activeCategory);

  return (
    <div className="bg-brand-cream min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-serif font-bold text-brand-charcoal mb-4">Our Product Range</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Browse our selection of authentic African and Ghanaian food products. Available for household use, retail shops, and wholesale supply.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                activeCategory === cat
                  ? 'bg-brand-green text-white shadow-md'
                  : 'bg-white text-brand-charcoal border border-brand-sand hover:border-brand-green hover:text-brand-green'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Product Grid */}
        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          <AnimatePresence>
            {filteredProducts.map((product) => (
              <motion.div
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                key={product.id}
                className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-shadow border border-brand-sand overflow-hidden flex flex-col"
              >
                <Link to={`/product/${product.id}`} className="block relative h-56 bg-white p-6 flex items-center justify-center group">
                  <div className="absolute inset-0 bg-brand-sand/30 group-hover:bg-brand-sand/50 transition-colors"></div>
                  <img 
                    src={product.image} 
                    alt={product.name} 
                    className="max-h-full max-w-full object-contain relative z-10 group-hover:scale-105 transition-transform duration-300 drop-shadow-sm"
                  />
                  {product.packageSize && (
                    <span className="absolute top-4 right-4 z-20 bg-brand-yellow text-white text-xs font-bold px-2 py-1 rounded">
                      {product.packageSize}
                    </span>
                  )}
                </Link>
                
                <div className="p-6 flex flex-col flex-grow">
                  <span className="text-xs font-medium text-brand-red uppercase tracking-wider mb-2">
                    {product.category}
                  </span>
                  <h3 className="text-lg font-bold text-brand-charcoal mb-2 line-clamp-2">
                    {product.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-6 line-clamp-3 flex-grow">
                    {product.description}
                  </p>
                  
                  <Link 
                    to={`/product/${product.id}`}
                    className="w-full text-center bg-transparent border-2 border-brand-green text-brand-green py-2.5 rounded-md font-semibold hover:bg-brand-green hover:text-white transition-colors"
                  >
                    View Details
                  </Link>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-500 text-lg">No products found in this category.</p>
          </div>
        )}

        {/* Wholesale CTA */}
        <div className="mt-20 bg-brand-green rounded-2xl p-8 md:p-12 text-center text-white shadow-lg relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 bg-pattern pointer-events-none"></div>
          <div className="relative z-10">
            <h2 className="text-2xl md:text-3xl font-serif font-bold mb-4">Looking for bulk quantities?</h2>
            <p className="mb-8 text-gray-200 max-w-2xl mx-auto">
              We supply African food shops, restaurants and distributors. Contact us for a full wholesale price list and availability.
            </p>
            <Link 
              to="/wholesale" 
              className="inline-block bg-brand-yellow text-brand-green font-bold px-8 py-3 rounded-md hover:bg-yellow-400 transition-colors"
            >
              Request Wholesale Info
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Products;